import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Alert from '@mui/material/Alert'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { format, subDays } from 'date-fns'
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined'
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined'
import KPICard from '../components/KPICard'
import InterventionFilters from '../components/InterventionFilters'
import CostBreakdownChart from '../components/CostBreakdownChart'
import MerchantBreakdownTable from '../components/MerchantBreakdownTable'
import { getAccess } from '../../../helpers/ReduxHelpers'
import { MOCK_COST } from '../mockData'
import { useGetCostAnalyticsQuery } from '../../../redux/interventionApiSlice'

const USE_MOCK = false

export default function CostAnalytics() {
  const userAccess = getAccess() || []
  const hasCostAccess = userAccess.includes('INTERVENTION_COST') || userAccess.includes('SA')

  const [filters, setFilters] = useState({
    type: 'BOTH',
    merchantOids: [],
    dateRange: { from: format(subDays(new Date(), 2), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') },
  })

  const noMerchant = !USE_MOCK && !filters.merchantOids?.length
  // Hook must run before the access-gate early return (React rules-of-hooks).
  const { data: apiData, isFetching } = useGetCostAnalyticsQuery(filters, { skip: USE_MOCK || !hasCostAccess || noMerchant })

  if (!hasCostAccess && !USE_MOCK) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <LockOutlinedIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant='h6' sx={{ color: 'text.secondary' }}>Access Restricted</Typography>
        <Typography variant='body2' sx={{ color: 'text.disabled', mt: 1 }}>
          You need the INTERVENTION_COST access module to view cost analytics.
        </Typography>
      </Box>
    )
  }

  const data = USE_MOCK ? MOCK_COST : apiData
  const isLoading = USE_MOCK ? false : isFetching
  const cb = data?.channelBreakdown || {}

  const channelRows = [
    { channel: 'WhatsApp', icon: <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366' }} />, totalCost: cb.whatsapp?.totalCost, count: cb.whatsapp?.count, unitCost: cb.whatsapp?.unitCost },
    { channel: 'SMS', icon: <SmsOutlinedIcon sx={{ fontSize: 16, color: '#5B8DEF' }} />, totalCost: cb.sms?.totalCost, count: cb.sms?.count, unitCost: cb.sms?.unitCost },
    { channel: 'NDR Calling', icon: <PhoneInTalkOutlinedIcon sx={{ fontSize: 16, color: '#FF6B35' }} />, totalCost: cb.ndr_calling?.totalCost, count: cb.ndr_calling?.count, unitCost: cb.ndr_calling?.avgCostPerCall },
    { channel: 'NPR Calling', icon: <PhoneInTalkOutlinedIcon sx={{ fontSize: 16, color: '#E85D3A' }} />, totalCost: cb.npr_calling?.totalCost, count: cb.npr_calling?.count, unitCost: cb.npr_calling?.avgCostPerCall },
  ]

  const merchantCols = [
    { key: 'merchantName', label: 'Merchant', format: 'text' },
    { key: 'totalCost', label: 'Total Cost', format: 'currency', align: 'right' },
    { key: 'costPerDelivered', label: 'Cost/Delivered', format: 'currency', align: 'right' },
  ]

  return (
    <Box>
      <Typography variant='h5' sx={{ mb: 2 }}>Cost Analytics</Typography>

      <Alert severity='info' sx={{ mb: 2 }}>
        Cost data is based on VAS billing deductions from merchant wallets. Unit costs: WhatsApp ₹0.60, SMS ₹0.20, Calling varies by provider.
      </Alert>

      <InterventionFilters filters={filters} onChange={setFilters} />

      {noMerchant ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant='body1' sx={{ color: 'text.disabled' }}>Select a merchant to view data</Typography>
        </Box>
      ) : <>

      <Box className='kpi-row'>
        <KPICard title='Total Intervention Cost' value={data?.totalCost} icon={<PaidOutlinedIcon />} color='#E85D3A' format='currency' loading={isLoading} />
        <KPICard title='Cost / Delivered' value={data?.costPerDelivered} icon={<PaidOutlinedIcon />} color='#0F4C81' format='currency' loading={isLoading} />
        <KPICard title='WhatsApp Cost' value={cb.whatsapp?.totalCost} icon={<WhatsAppIcon />} color='#25D366' format='currency' loading={isLoading} />
        <KPICard title='Calling Cost' value={(cb.ndr_calling?.totalCost || 0) + (cb.npr_calling?.totalCost || 0)} icon={<PhoneInTalkOutlinedIcon />} color='#FF6B35' format='currency' loading={isLoading} />
      </Box>

      <CostBreakdownChart data={data?.dailyTrend || []} loading={isLoading} />

      {/* Channel-wise table */}
      <Paper sx={{ p: 2.5, mt: 2 }}>
        <Typography variant='subtitle1' sx={{ mb: 1.5 }}>Cost by Channel</Typography>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Channel</TableCell>
                <TableCell align='right'>Total Cost</TableCell>
                <TableCell align='right'>Count</TableCell>
                <TableCell align='right'>Unit Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {channelRows.map(r => (
                <TableRow key={r.channel} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{r.icon} {r.channel}</Box>
                  </TableCell>
                  <TableCell align='right' sx={{ fontWeight: 600 }}>₹{r.totalCost?.toLocaleString('en-IN') || '—'}</TableCell>
                  <TableCell align='right'>{r.count?.toLocaleString('en-IN') || '—'}</TableCell>
                  <TableCell align='right'>₹{r.unitCost?.toFixed(2) || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ mt: 2 }}>
        <MerchantBreakdownTable
          data={data?.merchantBreakdown || []}
          columns={merchantCols}
          title='Cost by Merchant'
          loading={isLoading}
          defaultSort={{ field: 'totalCost', direction: 'desc' }}
        />
      </Box>
      </>}
    </Box>
  )
}
